import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Stethoscope,
  RefreshCw,
  Wrench,
  Share2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  Usb,
  Wifi,
  Bluetooth,
  Cpu,
  ShieldAlert,
  Globe,
  Loader2
} from 'lucide-react';
import {
  runDoctorScan,
  applyDoctorFix,
  DoctorScan,
  DoctorFinding,
  Severity,
  getAutopilot,
  setAutopilot,
  AutopilotState,
  netPing,
  netDns,
  netTcpCheck,
  netHttpCheck,
  parsePorts,
  shareOrCopy
} from '../services/fieldKit';
import { UsbSerialNative, isUsbSerialSupported } from '../services/usbSerial';
import { triggerHaptic } from '../services/purrSynthesizer';

const SEV_STYLE: Record<Severity, { ring: string; text: string; bg: string; icon: React.ReactNode }> = {
  error: { ring: 'border-red-500/40', text: 'text-red-300', bg: 'bg-red-500/10', icon: <XCircle className="w-4 h-4 text-red-400" /> },
  warn: { ring: 'border-amber-500/40', text: 'text-amber-200', bg: 'bg-amber-500/10', icon: <AlertTriangle className="w-4 h-4 text-amber-400" /> },
  info: { ring: 'border-sky-500/30', text: 'text-sky-200', bg: 'bg-sky-500/5', icon: <Info className="w-4 h-4 text-sky-400" /> },
  ok: { ring: 'border-emerald-500/30', text: 'text-emerald-200', bg: 'bg-emerald-500/5', icon: <CheckCircle2 className="w-4 h-4 text-emerald-400" /> }
};

const AREA_ICON: Record<string, React.ReactNode> = {
  usb: <Usb className="w-3 h-3" />,
  wifi: <Wifi className="w-3 h-3" />,
  bluetooth: <Bluetooth className="w-3 h-3" />,
  system: <Cpu className="w-3 h-3" />,
  policy: <ShieldAlert className="w-3 h-3" />
};

const scoreColor = (s: number) => (s >= 85 ? 'text-emerald-400' : s >= 60 ? 'text-amber-400' : 'text-red-400');

export const FieldDoctorPanel: React.FC = () => {
  const [scan, setScan] = useState<DoctorScan | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fixing, setFixing] = useState<string | null>(null);
  const [fixMsg, setFixMsg] = useState<Record<string, { ok: boolean; message: string }>>({});
  const [hideOk, setHideOk] = useState(true);
  const [autopilot, setAutopilotState] = useState<AutopilotState | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const scanSeq = useRef(0);

  const doScan = useCallback(async () => {
    const seq = ++scanSeq.current;
    setScanning(true);
    setError(null);
    try {
      const res = await runDoctorScan();
      if (seq === scanSeq.current) setScan(res);
    } catch (e: any) {
      if (seq === scanSeq.current) setError(e?.message || 'Scan failed. Is the Bastet service running?');
    } finally {
      if (seq === scanSeq.current) setScanning(false);
    }
  }, []);

  // Scan on open, then re-scan (debounced) whenever hardware appears/disappears so the screen is always current.
  useEffect(() => {
    doScan();
    getAutopilot().then(setAutopilotState);

    let timer: any = null;
    const handles: Array<{ remove: () => void }> = [];
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(doScan, 1200);
    };
    if (isUsbSerialSupported()) {
      for (const ev of ['usbAttached', 'usbDetached']) {
        UsbSerialNative.addListener(ev, schedule)
          .then((h: any) => handles.push(h))
          .catch(() => {});
      }
    }
    return () => {
      clearTimeout(timer);
      handles.forEach((h) => {
        try {
          h.remove();
        } catch {
          /* already removed */
        }
      });
    };
  }, [doScan]);

  const showToast = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 2600);
  };

  const onFix = async (f: DoctorFinding) => {
    if (!f.fix || fixing) return;
    triggerHaptic(25);
    setFixing(f.id);
    const res = await applyDoctorFix(f.fix);
    setFixMsg((prev) => ({ ...prev, [f.id]: { ok: res.ok, message: res.message } }));
    setFixing(null);
    // Settings deep-links need the user to come back; everything else can be verified right away.
    if (!res.launched) setTimeout(doScan, 800);
  };

  const onShare = async () => {
    if (!scan) return;
    const r = await shareOrCopy('Fissal Field Doctor report', scan.reportText);
    showToast(r === 'shared' ? 'Report shared' : r === 'copied' ? 'Report copied to clipboard' : 'Could not share the report');
  };

  const toggleAutopilot = async (key: 'enabled' | 'autoConnect' | 'autoAdb', value: boolean) => {
    triggerHaptic(15);
    const next = await setAutopilot({ [key]: value });
    if (next) setAutopilotState(next);
  };

  const findings = (scan?.findings || []).filter((f) => !(hideOk && f.severity === 'ok'));
  const okCount = (scan?.findings || []).filter((f) => f.severity === 'ok').length;

  return (
    <div className="space-y-4">
      {/* Score header */}
      <div className="glass-panel-gold rounded-2xl p-4 border border-fissal-gold/30 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`text-4xl font-black font-mono leading-none ${scan ? scoreColor(scan.score) : 'text-slate-500'}`}>
              {scan ? scan.score : '--'}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-xs font-bold text-white tracking-wider">
                <Stethoscope className="w-4 h-4 text-emerald-400" /> FIELD DOCTOR
              </div>
              <p className="text-[11px] text-slate-400 font-mono truncate">
                {scanning ? 'Scanning USB, Wi-Fi, Bluetooth, system...' : scan ? scan.summary : error ? 'Scan failed' : 'Ready'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={onShare}
              disabled={!scan}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/15 disabled:opacity-40 border border-white/10 text-slate-200"
              aria-label="Share report"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                triggerHaptic(20);
                doScan();
              }}
              disabled={scanning}
              className="px-3 py-2 rounded-xl bg-fissal-gold text-slate-950 text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"
            >
              {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Scan
            </button>
          </div>
        </div>

        {scan && (
          <div className="grid grid-cols-4 gap-1.5 text-[10px] font-mono">
            <Chip icon={<Usb className="w-3 h-3" />} label="USB" value={`${scan.usb?.deviceCount ?? 0}`} />
            <Chip
              icon={<Wifi className="w-3 h-3" />}
              label="Wi-Fi"
              value={scan.network?.connected ? (scan.network?.rssi != null ? `${scan.network.rssi} dBm` : 'up') : 'down'}
            />
            <Chip
              icon={<Bluetooth className="w-3 h-3" />}
              label="BT"
              value={scan.bluetooth?.enabled ? `${scan.bluetooth?.bondedCount ?? 0} paired` : 'off'}
            />
            <Chip icon={<Cpu className="w-3 h-3" />} label="Shizuku" value={scan.system?.shizukuPermission ? 'ready' : scan.system?.shizukuRunning ? 'auth?' : 'off'} />
          </div>
        )}
        {scan?.network?.ssid && (
          <p className="text-[10px] text-slate-500 font-mono truncate">
            {scan.network.ssid} · {scan.network.band} · {scan.network.linkSpeedMbps} Mbps{scan.network.gateway ? ` · gw ${scan.network.gateway}` : ''}
          </p>
        )}
      </div>

      {error && <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-200 text-xs p-3 font-mono">{error}</div>}

      {/* Findings */}
      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <span className="text-[11px] font-bold text-slate-300 tracking-wider">FINDINGS</span>
          {okCount > 0 && (
            <button onClick={() => setHideOk((v) => !v)} className="text-[10px] text-slate-400 underline underline-offset-2">
              {hideOk ? `Show ${okCount} healthy` : 'Hide healthy'}
            </button>
          )}
        </div>
        {scan && findings.length === 0 && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 text-emerald-200 text-xs p-3">
            Nothing needs attention. Plug in a device and this screen updates by itself.
          </div>
        )}
        {findings.map((f) => {
          const st = SEV_STYLE[f.severity];
          const fm = fixMsg[f.id];
          return (
            <div key={f.id} className={`rounded-xl border ${st.ring} ${st.bg} p-3 space-y-1.5`}>
              <div className="flex items-start gap-2">
                <div className="mt-0.5">{st.icon}</div>
                <div className="min-w-0 flex-1">
                  <div className={`text-xs font-bold ${st.text}`}>{f.title}</div>
                  {f.detail && <p className="text-[11px] text-slate-400 leading-snug mt-0.5">{f.detail}</p>}
                </div>
                <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-500 shrink-0">
                  {AREA_ICON[f.area]}
                  {f.area}
                </span>
              </div>
              {f.fix && (
                <div className="flex items-center gap-2 pl-6">
                  <button
                    onClick={() => onFix(f)}
                    disabled={!!fixing}
                    className="px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10 text-[11px] font-bold text-white flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {fixing === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3 text-fissal-gold" />}
                    {f.fix.label}
                  </button>
                  {fm && <span className={`text-[10px] font-mono ${fm.ok ? 'text-emerald-300' : 'text-amber-300'}`}>{fm.message}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Plug & play settings */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-2.5">
        <div className="text-[11px] font-bold text-slate-300 tracking-wider">PLUG &amp; PLAY</div>
        <Toggle
          label="Autopilot"
          hint="Ask for USB permission the moment a serial adapter or Android device is plugged in"
          checked={!!autopilot?.enabled}
          onChange={(v) => toggleAutopilot('enabled', v)}
        />
        <Toggle
          label="Auto-connect serial"
          hint="Open serial adapters automatically at the remembered (or detected) baud rate. DTR/RTS stay low so boards aren't reset"
          checked={!!autopilot?.autoConnectSerial}
          disabled={!autopilot?.enabled}
          onChange={(v) => toggleAutopilot('autoConnect', v)}
        />
        <Toggle
          label="Auto-connect ADB"
          hint="Off by default: connecting makes the other device show an 'Allow USB debugging?' prompt"
          checked={!!autopilot?.autoConnectAdb}
          disabled={!autopilot?.enabled}
          onChange={(v) => toggleAutopilot('autoAdb', v)}
        />
      </div>

      <NetTools gateway={scan?.network?.gateway} />

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full bg-slate-900/95 border border-white/15 text-xs text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

const Chip: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="rounded-lg bg-black/40 border border-white/10 px-2 py-1.5 flex flex-col items-center gap-0.5">
    <span className="flex items-center gap-1 text-slate-500">
      {icon}
      {label}
    </span>
    <span className="text-slate-100 font-bold truncate max-w-full">{value}</span>
  </div>
);

const Toggle: React.FC<{ label: string; hint: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }> = ({
  label,
  hint,
  checked,
  disabled,
  onChange
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`w-full flex items-center justify-between gap-3 text-left ${disabled ? 'opacity-40' : ''}`}
  >
    <span className="min-w-0">
      <span className="block text-xs font-bold text-slate-100">{label}</span>
      <span className="block text-[10px] text-slate-500 leading-snug">{hint}</span>
    </span>
    <span className={`w-10 h-6 rounded-full shrink-0 flex items-center px-0.5 transition-colors ${checked ? 'bg-emerald-500' : 'bg-slate-700'}`}>
      <span className={`w-5 h-5 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </span>
  </button>
);

// ───────────────────────── network toolbox ─────────────────────────

const NetTools: React.FC<{ gateway?: string }> = ({ gateway }) => {
  const [host, setHost] = useState('');
  const [ports, setPorts] = useState('22 80 443 554 8080');
  const [out, setOut] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const target = host.trim() || gateway || '1.1.1.1';

  const run = async (name: string, fn: () => Promise<any>) => {
    if (busy) return;
    triggerHaptic(15);
    setBusy(name);
    setOut(`${name} ${target} ...`);
    try {
      setOut(format(name, await fn()));
    } catch (e: any) {
      setOut(`${name} failed: ${e?.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const format = (name: string, r: any): string => {
    if (!r) return 'No response.';
    if (name === 'ping') {
      if (r.raw) return r.raw;
      return r.ok ? `reachable, ~${r.rttAvgMs} ms (${r.method || 'icmp'})` : `unreachable${r.error ? `: ${r.error}` : ''}`;
    }
    if (name === 'dns') {
      return r.ok ? `${r.host} → ${(r.addresses || []).join(', ')}  (${r.ms} ms)` : `${r.host}: ${r.error}  (${r.ms} ms)`;
    }
    if (name === 'ports') {
      if (!r.ok) return r.error || 'Port check failed.';
      return (r.results || []).map((p: any) => `${String(p.port).padStart(5)}  ${String(p.state).padEnd(16)} ${p.ms} ms`).join('\n');
    }
    if (name === 'http') {
      return r.ok === undefined && r.error ? r.error : JSON.stringify(r, null, 2);
    }
    return JSON.stringify(r, null, 2);
  };

  const btn = 'px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10 text-[11px] font-bold text-white disabled:opacity-50';

  return (
    <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-2.5">
      <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-300 tracking-wider">
        <Globe className="w-3.5 h-3.5 text-sky-400" /> NETWORK TOOLS
      </div>
      <input
        value={host}
        onChange={(e) => setHost(e.target.value)}
        placeholder={gateway ? `host / IP / URL (default: gateway ${gateway})` : 'host / IP / URL (default: 1.1.1.1)'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white placeholder:text-slate-600 focus:outline-none focus:border-fissal-gold/60"
      />
      <div className="flex flex-wrap gap-1.5">
        <button className={btn} disabled={!!busy} onClick={() => run('ping', () => netPing(target))}>
          Ping
        </button>
        <button className={btn} disabled={!!busy} onClick={() => run('dns', () => netDns(target))}>
          DNS
        </button>
        <button className={btn} disabled={!!busy} onClick={() => run('ports', () => netTcpCheck(target, parsePorts(ports)))}>
          Ports
        </button>
        <button
          className={btn}
          disabled={!!busy}
          onClick={() => run('http', () => netHttpCheck(/^https?:\/\//i.test(target) ? target : `https://${target}`))}
        >
          HTTP(S)
        </button>
      </div>
      <input
        value={ports}
        onChange={(e) => setPorts(e.target.value)}
        placeholder="ports (space/comma separated, max 32)"
        inputMode="numeric"
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-[11px] font-mono text-slate-300 placeholder:text-slate-600 focus:outline-none"
      />
      {out && <pre className="bg-black/60 border border-white/10 rounded-lg p-2.5 text-[10px] leading-relaxed text-emerald-200 font-mono whitespace-pre-wrap break-words max-h-56 overflow-auto">{out}</pre>}
    </div>
  );
};
